import { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Switch } from "@progress/kendo-react-inputs";
import { UserPreferences } from "../../data/employeeData";
import { saveUserPreferences, useUserPreferences } from "../../helpers/userPreferences";

interface UserPreferencesDialogProps {
  onClose: () => void;
}

/**
 * Per-user portal settings, opened from the user's name in the top right.
 * Saved against the employee record so they follow the user to any machine.
 */
const UserPreferencesDialog = ({ onClose }: UserPreferencesDialogProps) => {
  const saved = useUserPreferences();

  const [preferences, setPreferences] = useState<UserPreferences>(saved);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage("");

    try {
      await saveUserPreferences(preferences);
      onClose();
    } catch (error) {
      console.error("Could not save preferences:", error);
      setErrorMessage("Your preferences could not be saved. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog title="Preferences" onClose={onClose} width={440}>
      <div style={{ padding: "1rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1rem",
          }}
        >
          <div>
            <label
              style={{
                display: "block",
                fontWeight: 600,
                fontSize: "14px",
                color: "#323130",
              }}
            >
              Remember filters
            </label>
            <p
              style={{
                margin: "0.25rem 0 0",
                fontSize: "12px",
                color: "#605e5c",
                maxWidth: "18rem",
              }}
            >
              Keep the filters, sorting and page size you set on list screens as
              you move between sections. Turn this off to start each screen
              clean.
            </p>
          </div>

          <Switch
            checked={preferences.rememberFilters}
            disabled={isSaving}
            onChange={(e) =>
              setPreferences({ ...preferences, rememberFilters: e.value })
            }
          />
        </div>

        {errorMessage ? (
          <p style={{ margin: "1rem 0 0", fontSize: "12px", color: "#a4262c" }}>
            {errorMessage}
          </p>
        ) : null}
      </div>

      <DialogActionsBar>
        <Button themeColor="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default UserPreferencesDialog;
